import React, { useEffect, useState, useRef } from "react";

import PercentField from "../fields/percent_field";
import NumberField from "../fields/number_field";
import SelectField from "../fields/select_field";

import { default_loan } from "../../schema/loan";

import LOAN_TYPES, {
  REFINANCE_LOAN_TYPES,
  JUMBO,
  HELOC,
  FIXED_RATE_SECOND
} from "../../constants/loan_types";
import { LOAN_TERMS, ARM_TERMS, LOAN_TERMS_ORDER } from "../../constants/loan_terms";
import LOAN_PURPOSE, {
  REFINANCE_LOAN_PURPOSES
} from "../../constants/loan_purpose";

import {
  ARM_CAPS,
  ARM_MARGINS,
  ARM_INDEX_TYPES
} from "../../constants/loan_arm_properties";
import Section from "../ui/section";
import CurrencyField from "../fields/currency_field";
import { useDocument } from "../loan_tools/loan_tool";
import { REFINANCE } from "../../constants/document_types";
import { PRIMARY_RESIDENCE } from "../../constants/property_classification";
import Decimal from "decimal.js";
import {
  BUYDOWN_TYPES,
  BUYDOWN_PAID_BY,
  BUYDOWN_DISCLOSE
} from "../../constants/buydown";
import { NO, YES } from "../../constants/yes_no";
import { INCLUDE_IN_DTI } from "../../constants/account_dispositions";

export default function EditLoanPage({ loan, loanIndex, showMainPage, onChangeLoan }) {
  const [
    { applicant, loans, accountSummaries, property, property_to_be_sold },
    ,
    { documentType },
    { saveDocument }
  ] = useDocument();
  const [loanEdit, setLoanEdit] = useState({ ...default_loan, ...loan });
  const {
    type,
    term,
    arm_caps,
    arm_margin,
    arm_index_type,
    arm_index_percent,
    jumbo_months_of_reserves,
    jumbo_reserves_for_other_properties,
    jumbo_arm_months_of_reserves,
    buydown_type,
    buydown_paid_by,
    buydown_disclose_product,
    buydown_step_months,
    purpose
  } = loanEdit;

  const isRefinance = documentType === REFINANCE;
  const isPrimaryResidence = property.classification === PRIMARY_RESIDENCE;
  // Show which loan is being edited so the LO isn't left guessing (matches the
  // "Edit Loan N" header on the LE Price Eng / LE Price Eng Opt pages). When all
  // loans share one type, the column represents every loan, so read "Loans 1-5".
  const editLoanTitle =
    applicant.all_loan_types_the_same === YES
      ? "Edit Loans 1-5"
      : `Edit Loan ${(loanIndex || 0) + 1}`;
  const callback = useRef();

  useEffect(() => {
    callback.current = () => {
      if (loanEdit.is_modified) {
        if (validLoan()) {
          if (
            window.confirm(
              "You have unsaved data in loan form. Would you like to save it?"
            )
          ) {
            save();
          } else {
            // do nothing
          }
        } else {
          alert(
            "Not all necessary fields were entered. Therefore changes would be dismissed."
          );
        }
      }
    };
  }, [loanEdit]);

  useEffect(() => {
    //do nothing

    return () => {
      callback.current();
    };
  }, []);

  function onChange(updatedState) {
    setLoanEdit({ ...loanEdit, ...updatedState, is_modified: true });
  }

  function save() {
    delete loanEdit.is_modified;
    if (!loan && applicant.different_loan_options_for_paying_off_debt === NO) {
      // Dispositions need to be set
      const firstLoan = loans[0];
      if (firstLoan) {
        loanEdit.account_dispositions = { ...firstLoan.account_dispositions };
      } else {
        Object.keys(accountSummaries).forEach(accountSummary => {
          loanEdit.account_dispositions[accountSummary] = INCLUDE_IN_DTI;
        });
      }
    }
    onChangeLoan(loanEdit);
    saveDocument();
    showMainPage();
  }

  function cancel() {
    showMainPage();
  }

  function validateNonEmptyValue(value) {
    return value !== "";
  }

  function validLoan() {
    const validationFields = [loanEdit.type, loanEdit.term, loanEdit.purpose];

    const isARMTerm = ARM_TERMS.includes(loanEdit.term);

    if (isARMTerm) {
      validationFields.push(
        loanEdit.arm_caps,
        loanEdit.arm_margin,
        loanEdit.arm_years,
        loanEdit.arm_index_type,
        loanEdit.arm_index_percent
      );
    }

    if (loanEdit.type === JUMBO) {
      validationFields.push(
        loanEdit.jumbo_months_of_reserves,
        loanEdit.jumbo_reserves_for_other_properties,
        loanEdit.jumbo_arm_months_of_reserves
      );
      if (isARMTerm) {
        validationFields.push(loanEdit.jumbo_arm_months_of_reserves);
      }
    }

    return validationFields.every(validateNonEmptyValue);
  }

  const termOptions = LOAN_TERMS_ORDER.map(key => (
    <option key={key} value={key}>
      {LOAN_TERMS[key]}
    </option>
  ));

  return (
    <Section className="edit-loan-type" name={editLoanTitle}>
      <div className="half-width">
        <SelectField
          name="Loan Type"
          fieldName="type"
          value={type}
          onChange={onChange}
          selectOptions={isRefinance ? REFINANCE_LOAN_TYPES : LOAN_TYPES}
          sortLabel
        />

        <SelectField
          name="Loan Term"
          fieldName="term"
          value={term}
          onChange={onChange}
        >
          {termOptions}
        </SelectField>

        <SelectField
          name="Buydown Loans"
          fieldName="buydown_type"
          value={buydown_type}
          onChange={onChange}
          selectOptions={BUYDOWN_TYPES}
          showDefaultOption
        />

        {buydown_type && (
          <div>
            <SelectField
              name="Who pays the buydown?"
              fieldName="buydown_paid_by"
              value={buydown_paid_by}
              onChange={onChange}
              selectOptions={BUYDOWN_PAID_BY}
              showDefaultOption
            />
            <SelectField
              name="Disclose LE / CD Product"
              fieldName="buydown_disclose_product"
              value={buydown_disclose_product}
              onChange={onChange}
              selectOptions={BUYDOWN_DISCLOSE}
              showDefaultOption
            />
            <NumberField
              name="Buydown Step (months)"
              fieldName="buydown_step_months"
              value={buydown_step_months}
              onChange={onChange}
            />
          </div>
        )}

        {ARM_TERMS.includes(term) && (
          <div>
            <SelectField
              name="ARM Caps"
              fieldName="arm_caps"
              value={arm_caps}
              onChange={onChange}
              selectOptions={ARM_CAPS}
            />
            <SelectField
              name="ARM Margin"
              fieldName="arm_margin"
              value={arm_margin}
              onChange={onChange}
              selectOptions={ARM_MARGINS}
            />

            <SelectField
              name="ARM Index Type"
              fieldName="arm_index_type"
              value={arm_index_type}
              onChange={onChange}
              selectOptions={ARM_INDEX_TYPES}
              sortLabel
            />

            <PercentField
              name="ARM Index"
              fieldName="arm_index_percent"
              fixedDigits={3}
              value={arm_index_percent}
              onChange={onChange}
            />
          </div>
        )}

        <SelectField
          name="Purpose"
          fieldName="purpose"
          value={purpose}
          onChange={onChange}
          selectOptions={isRefinance ? REFINANCE_LOAN_PURPOSES : LOAN_PURPOSE}
          showDefaultOption
        />

        {isRefinance && (type === HELOC || type === FIXED_RATE_SECOND) && (
          <>
            <div className="down-payment-section-title" style={{ marginTop: "14px" }}>
              Current Housing Expenses
            </div>
            <CurrencyField
              readOnly
              name="Property Tax (Monthly)"
              value={new Decimal(property.refinance_property_tax_amount || "0").div(
                property.refinance_property_tax_frequency === "2" ? 3
                  : property.refinance_property_tax_frequency === "3" ? 12 : 1
              )}
            />
            <CurrencyField
              readOnly
              name="HOI (Monthly)"
              value={new Decimal(property.home_owners_insurance || "0").div(
                property.home_owners_insurance_frequency === "2" ? 3
                  : property.home_owners_insurance_frequency === "3" ? 12 : 1
              )}
            />
            <CurrencyField
              readOnly
              name="HOA 1 (Monthly)"
              value={new Decimal(property.hoa1 || "0").div(
                property.hoa1_frequency === "2" ? 3
                  : property.hoa1_frequency === "3" ? 12 : 1
              )}
            />
            <CurrencyField
              readOnly
              name="HOA 2 (Monthly)"
              value={new Decimal(property.hoa2 || "0").div(
                property.hoa2_frequency === "2" ? 3
                  : property.hoa2_frequency === "3" ? 12 : 1
              )}
            />
            {property.mello_roos === "2" && (
              <CurrencyField
                readOnly
                name="Mello Roos (Monthly)"
                value={new Decimal(property.mello_roos_amount || "0").div(
                  property.mello_roos_frequency === "2" ? 3
                    : property.mello_roos_frequency === "3" ? 12 : 1
                )}
              />
            )}
            <CurrencyField
              readOnly
              name="Principal Balance"
              value={isPrimaryResidence
                ? new Decimal(property_to_be_sold.first_loan_balance || "0")
                    .plus(property_to_be_sold.second_loan_balance || "0")
                : property.principal_balance}
            />
            <CurrencyField
              readOnly
              name="1st Mortgage Payment"
              value={isPrimaryResidence
                ? property_to_be_sold.first_current_monthly_payment_principal
                : property.first_mortgage}
            />
            <CurrencyField
              readOnly
              name="2nd Mortgage Payment"
              value={isPrimaryResidence
                ? property_to_be_sold.second_current_monthly_payment
                : property.second_mortgage}
            />
          </>
        )}

        {type === JUMBO && (
          <div>
            <NumberField
              name="Mo. Of Reserves"
              fieldName="jumbo_months_of_reserves"
              value={jumbo_months_of_reserves}
              onChange={onChange}
            />
            <NumberField
              name="Reserves for other properties"
              fieldName="jumbo_reserves_for_other_properties"
              value={jumbo_reserves_for_other_properties}
              onChange={onChange}
            />
            {ARM_TERMS.includes(term) && (
              <NumberField
                name="Mo. of Reserves ARM Loans"
                fieldName="jumbo_arm_months_of_reserves"
                value={jumbo_arm_months_of_reserves}
                onChange={onChange}
              />
            )}
          </div>
        )}
      </div>

      <button className="button" onClick={cancel}>
        Cancel
      </button>
      <button className="button" disabled={!validLoan()} onClick={save}>
        Save
      </button>
    </Section>
  );
}
